/**
 * PRDClassificationBadge - Displays PRD scenario type and project size with guidance.
 *
 * Shows the classified scenario type and project size to help users understand
 * what type of PRD is being generated and why.
 */

import React, { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Chip } from '@heroui/react';
import clsx from 'clsx';
import { ChevronDown, ChevronUp, Info, Loader2 } from 'lucide-react';

interface PRDClassificationBadgeProps {
  scenarioType?: string | null;
  projectSize?: string | null;
  classificationConfidence?: string | null;
  classificationReasoning?: string | null;
  isLoading?: boolean;
}

// Human-readable labels for scenario types
const SCENARIO_LABELS: Record<string, string> = {
  frontend_feature: 'Frontend Feature',
  backend_api: 'Backend API',
  fullstack_feature: 'Fullstack Feature',
  fullstack_application: 'Fullstack Application',
  bugfix: 'Bug Fix',
  refactor_tech_debt: 'Refactor / Tech Debt',
  integration_third_party: 'Third-Party Integration',
  infra_devops: 'Infrastructure / DevOps',
  data_pipeline_reporting: 'Data Pipeline / Reporting',
  ai_feature: 'AI Feature',
};

// Human-readable labels for project sizes
const SIZE_LABELS: Record<string, string> = {
  task: 'Task',
  feature: 'Feature',
  epic: 'Epic',
  initiative: 'Initiative',
};

// Descriptions for each scenario type
const SCENARIO_DESCRIPTIONS: Record<string, string> = {
  frontend_feature: 'UI components, pages, and user interactions',
  backend_api: 'Server endpoints, data processing, and business logic',
  fullstack_feature: 'End-to-end features spanning UI and API',
  fullstack_application: 'Complete new application with architecture, database, and infrastructure',
  bugfix: 'Correcting existing behavior',
  refactor_tech_debt: 'Improving code quality without changing behavior',
  integration_third_party: 'Connecting to external services',
  infra_devops: 'Deployment, infrastructure, and CI/CD',
  data_pipeline_reporting: 'Data processing, analytics, and reporting',
  ai_feature: 'AI agents, LLM integrations, and intelligent features',
};

// Descriptions for each project size
const SIZE_DESCRIPTIONS: Record<string, string> = {
  task: '1-4 hours, single change, 1-3 files',
  feature: '1-3 days, small feature, one module',
  epic: '1-2 weeks, large feature, multiple modules',
  initiative: 'Weeks to months, multi-system change',
};

export function PRDClassificationBadge({
  classificationConfidence,
  classificationReasoning,
  isLoading = false,
  projectSize,
  scenarioType,
}: PRDClassificationBadgeProps): React.JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState(false);

  // Show loading state ONLY if we're actively loading AND don't have data yet
  // If we have data (scenarioType and projectSize), show it even if isLoading is true
  // This prevents the skeleton from showing after data has been received but isLoading
  // hasn't updated yet (race condition fix)
  const hasClassificationData = scenarioType && projectSize;
  const shouldShowLoading = isLoading && !hasClassificationData;

  if (shouldShowLoading) {
    // Show loading skeleton only when actively loading without data
    return (
      <Card className={clsx('bg-surface-elevated', 'border-b', 'border-default')}>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-default-500" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">PRD Type:</span>
              <div className="flex items-center gap-2">
                <div className="h-6 w-24 animate-pulse bg-default-200 dark:bg-default-100 rounded-md" />
                <span className="text-sm text-default-400">•</span>
                <div className="h-6 w-16 animate-pulse bg-default-200 dark:bg-default-100 rounded-md" />
              </div>
              <Loader2 className="w-4 h-4 animate-spin text-default-400 ml-1" />
              <span className="text-xs text-default-500">Classifying...</span>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  // If no loading and no data, don't render
  if (!hasClassificationData) {
    return null;
  }

  const scenarioLabel = SCENARIO_LABELS[scenarioType] || scenarioType;
  const sizeLabel = SIZE_LABELS[projectSize] || projectSize;
  const scenarioDescription = SCENARIO_DESCRIPTIONS[scenarioType] || '';
  const sizeDescription = SIZE_DESCRIPTIONS[projectSize] || '';

  // Map confidence to Hero UI color
  const confidenceColor =
    classificationConfidence === 'high'
      ? 'success'
      : classificationConfidence === 'medium'
        ? 'warning'
        : 'default';

  const confidenceLabel =
    classificationConfidence === 'high'
      ? 'High'
      : classificationConfidence === 'medium'
        ? 'Medium'
        : 'Low';

  const hasDetails = scenarioDescription || sizeDescription || classificationReasoning;

  const expandIcon = isExpanded ? (
    <ChevronUp className="w-3 h-3" />
  ) : (
    <ChevronDown className="w-3 h-3" />
  );

  return (
    <Card className={clsx('bg-surface-elevated', 'border-b', 'border-default')}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <Info className="w-5 h-5 text-default-500" />
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">PRD Type:</span>
            <Chip color="primary" size="sm" variant="flat">
              {scenarioLabel}
            </Chip>
            <span className="text-sm text-default-400">•</span>
            <Chip color="secondary" size="sm" variant="flat">
              {sizeLabel}
            </Chip>
            {classificationConfidence && (
              <>
                <span className="text-sm text-default-400">•</span>
                <Chip color={confidenceColor} size="sm" variant="flat">
                  {confidenceLabel} confidence
                </Chip>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      {hasDetails && (
        <CardBody className="pt-0">
          <Button
            variant="light"
            size="sm"
            onPress={() => setIsExpanded(!isExpanded)}
            endContent={expandIcon}
          >
            {isExpanded ? 'Hide details' : 'Show details'}
          </Button>

          {isExpanded && (
            <div className={clsx('text-xs', 'text-default-600', 'space-y-1.5', 'mt-2')}>
              {scenarioDescription && (
                <div>
                  <span className="font-medium">{scenarioLabel}:</span> {scenarioDescription}
                </div>
              )}
              {sizeDescription && (
                <div>
                  <span className="font-medium">{sizeLabel}:</span> {sizeDescription}
                </div>
              )}
              {classificationReasoning && (
                <div
                  className={clsx(
                    'pt-2',
                    'border-t',
                    'border-default-200',
                    'text-default-500',
                    'italic',
                  )}
                >
                  {classificationReasoning}
                </div>
              )}
            </div>
          )}
        </CardBody>
      )}
    </Card>
  );
}
